import React from 'react'
import { type ProductFamily, type WebProduct } from '../../common/types/webshop.types'
import currency from 'currency.js';
import { formatCurrency } from '../../common/js/currency-utility';

import { useTranslation } from 'react-i18next'

import { getUpgradeOption, type UpgradeOption } from '../data/upgrade-options'

import styles from '../styles/type-select.module.css'

type Props = {
    currencyIso: string,
    family: ProductFamily,
    host: string,
    selected: string,
    onChange: Function
}

export default function TypeSelection({currencyIso, family, host, selected, onChange}: Props) {
    const { t } = useTranslation('pricing-tool');

    if (!family){
        return <></>
    }
    const monthly = fetchVariant(family, host, 'New Monthly Subscription');
    const annual = fetchVariant(family, host, 'New Annual Subscription');
    const perpetual = fetchVariant(family, host, 'New Permanent License');
    const upgradeOption = getUpgradeOption(family.name);
    const upgrade = upgradeOption ? fetchVariant(family, host, upgradeOption.productType) : undefined;

    const handleTypeChange = (event: React.MouseEvent<HTMLDivElement>) => {
        const type = event.currentTarget.dataset.type;
        onChange(type);
    }

    const prods = [monthly, annual, perpetual, upgrade];
    const options : React.JSX.Element[] = [];
    prods.forEach((p: WebProduct) => {
        if (!p){
            return;
        }

        const isSelected = p.type == selected;
        const typeLabel = variantLabel(p.type, t, upgradeOption);
        const listPrice = currency(p.listPrices[currencyIso]);
        const unitPrice = currency(p.prices[currencyIso]);
        const isDiscounted = p.price != p.listPrice;
        const isSubscription = p.type.includes('Subscription');
        const isMonthly = p.type.includes('Monthly');

        const displayedListPrice = p.type == 'New Annual Subscription' ?  listPrice.divide(12) : listPrice;
        const displayedUnitPrice = p.type == 'New Annual Subscription' ? unitPrice.divide(12) : unitPrice;

        let listPriceFormatted = formatCurrency(displayedListPrice, currencyIso);
        let unitPriceFormatted = formatCurrency(displayedUnitPrice, currencyIso);

        if (isSubscription){
            listPriceFormatted += '/' + t('month');
            unitPriceFormatted += '/' + t('month');
        }

        const selectedClass = isSelected ? `${styles.selected}` : '';

        options.push((
            <div data-type={p.type} onClick={handleTypeChange} className={`box ${styles.typeOption} ${selectedClass}`}>
                <h6 className={styles.typeLabel}>{typeLabel}</h6>
                {isDiscounted && (
                    <>
                    <span className={styles.priceTextDiscount}>{unitPriceFormatted}</span>
                    <br />
                    <span className={isMonthly ? styles.monthlyListPriceText : styles.listPriceText}>
                        {isMonthly ? `(Renews at ${listPriceFormatted})` : listPriceFormatted}
                    </span>
                    </>
                )}
                {!isDiscounted && (
                    <span className={styles.priceText}>{listPriceFormatted}</span>
                )}
            </div>
        ))
    });


  return (
    <div className={styles.typeOptions}>
        {options}
    </div>
  )
}

const fetchVariant = (family: ProductFamily, host: string, typeName: string) => {
    return family.products.find((p : WebProduct) => {
        return p.type == typeName && p.host == host;
    });
}

const variantLabel = (type: string, t: any, upgradeOption: UpgradeOption | null) => {
    switch(type){
        case 'New Annual Subscription':
            return t('productTypes.annual');
        case 'New Monthly Subscription':
            return t('productTypes.monthly');
        case 'New Permanent License':
            return t('productTypes.perpetual');
    }
    if (upgradeOption && type == upgradeOption.productType){
        return t('productTypes.' + upgradeOption.labelKey);
    }
}